import { useEffect } from "react";
import AdminPublicLayout from "@/layouts/AdminPublicLayout";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Users,
  BarChart3,
  ArrowRight,
  CheckCircle, Link, Building, Smartphone, Globe, Shield, Zap, Clock, Users2, TrendingUp, CalendarCheck, CreditCard, MessageCircle, Star, Award, Heart, MapPin, Phone, Mail, Instagram, Facebook, Twitter, Youtube
} from "lucide-react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useState } from "react";
import ContactDialog from "@/components/dialogs/ContactDialog";
import { Card, CardContent } from '@/components/ui/card';

const Index = () => {
  const { t } = useTranslation();
  const [isContactOpen, setIsContactOpen] = useState(false);

  const features = [
    {
      icon: <Calendar className="w-6 h-6 text-primary" />,
      title: t("landing.classManagementTitle"),
      description: t("landing.classManagementDescription")
    },
    {
      icon: <Users className="w-6 h-6 text-primary" />,
      title: t("landing.clientManagementTitle"),
      description: t("landing.clientManagementDescription")
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-primary" />,
      title: t("landing.businessAnalyticsTitle"),
      description: t("landing.businessAnalyticsDescription")
    }
  ];

  const benefits = [
    t("landing.benefit1"),
    t("landing.benefit2"),
    t("landing.benefit3"),
    t("landing.benefit4"),
    t("landing.benefit5"),
    t("landing.benefit6")
  ];

  function FitCalendarBadge() {
    useEffect(() => {
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://b.sf-syn.com/badge_js?sf_id=3900505&variant_id=sf";
      document.body.appendChild(script);

      // Cleanup
      return () => {
        document.body.removeChild(script);
      };
    }, []);

    return (
      <div className="sf-root" data-id="3900505" data-badge="light-default" data-variant-id="sf" style={{ width: "125px" }}>
        <a href="https://sourceforge.net/software/product/FitCalendar/" target="_blank" rel="noopener noreferrer">
          FitCalendar Reviews
        </a>
      </div>
    );
  }

  return (
    <AdminPublicLayout>
      <Helmet>
        <title>Yoga & Fitness Booking Software | FitCalendar - Free Studio Management</title>
        <link rel="canonical" href="https://fitcalendar.praxipro.io/" />
        <meta name="title" content="Yoga & Fitness Booking Software | FitCalendar - Free Studio Management" />
        <meta name="description"
          content="Free online booking and class management software for yoga, Pilates, CANTIENICA®, and fitness studios. Launch your schedule in minutes with FitCalendar. No credit card required." />
        <meta name="keywords" content="yoga studio software, fitness booking software, pilates studio management, CANTIENICA scheduling, online class booking, studio management software, fitness studio booking, yoga class scheduling" />
        <meta name="author" content="PraxiPro" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#3b82f6" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fitcalendar.praxipro.io/" />
        <meta property="og:title" content="Yoga & Fitness Booking Software | FitCalendar - Free Studio Management" />
        <meta property="og:description"
          content="Manage your yoga, CANTIENICA®, pilates, or fitness studio online. Book classes, manage schedules, and grow your business with FitCalendar. Start your free trial today." />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="FitCalendar Studio Software" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://fitcalendar.praxipro.io/" />
        <meta name="twitter:title" content="Yoga & Fitness Booking Software | FitCalendar - Free Studio Management" />
        <meta name="twitter:description"
          content="Free booking software for yoga, CANTIENICA® and pilates studios. Create schedules, manage classes, and accept online bookings easily. No setup fees." />
        <meta name="twitter:image" content="/og-image.png" />
        <meta name="twitter:creator" content="@PraxiPro" />
        <meta name="twitter:site" content="@PraxiPro" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "FitCalendar",
            "description": "Free online booking and class management software for yoga, Pilates, CANTIENICA®, and fitness studios.",
            "url": "https://fitcalendar.praxipro.io/",
            "image": "/og-image.png",
            "author": {
              "@type": "Organization",
              "name": "PraxiPro"
            },
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "156"
            }
          })}
        </script>
        
        {/* Breadcrumb structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://fitcalendar.praxipro.io/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Yoga Studio Software",
                "item": "https://fitcalendar.praxipro.io/product/yoga-studio-software/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Pilates Studio Software",
                "item": "https://fitcalendar.praxipro.io/product/pilates-studio-software/"
              }
            ]
          })}
        </script>
        
        {/* Article structured data for blog content */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Yoga & Fitness Studio Management Software",
            "description": "Learn how FitCalendar helps yoga, pilates, and fitness studios manage bookings, clients, and grow their business efficiently.",
            "image": "/og-image.png",
            "author": {
              "@type": "Organization",
              "name": "PraxiPro"
            },
            "publisher": {
              "@type": "Organization",
              "name": "PraxiPro",
              "logo": {
                "@type": "ImageObject",
                "url": "/images/logo.webp"
              }
            },
            "datePublished": "2025-07-28",
            "dateModified": "2025-08-13"
          })}
        </script>
        
        {/* Local Business structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "PraxiPro",
            "description": "Studio management software for yoga, pilates, CANTIENICA, and fitness studios.",
            "url": "https://fitcalendar.praxipro.io/",
            "logo": "/images/logo.webp",
            "sameAs": [
              "https://www.facebook.com/praxipro",
              "https://www.instagram.com/praxipro",
              "https://www.youtube.com/@praxipro"
            ]
          })}
        </script>
        
        <link rel="preload" as="image" href="/images/real-time-fitness-studio-dashboard.png" />
        <link rel="preload" as="image" href="/images/benefits.png" />
        <link rel="preload" as="image" href="/images/raum-fur-haltung-bewegung-und-balance.webp" />
        <link rel="preload" as="image" href="/images/physiotherapie-karlheinz-grubel.webp" />
        
        {/* Favicon and icons */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
      </Helmet>
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-50 to-indigo-50 -mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" itemProp="name">
                {t("landing.heroTitle")}
              </h1>
              <p className="text-lg text-gray-600 mb-8" itemProp="description">
                {t("landing.heroDescription")}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-secondary text-white px-8"
                  onClick={() => setIsContactOpen(true)}
                  itemProp="potentialAction"
                  itemScope
                  itemType="https://schema.org/Action"
                >
                  <span itemProp="name">{t("landing.ctaText")}</span> <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-white"
                  onClick={() => window.location.href = "/pricing/"}
                >
                  {t("landing.viewPricing")}
                </Button>
              </div>
              <div className="mt-6 flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-yellow-500" />
                  4.8/5 {t("landing.rating")}
                </span>
                <span>•</span>
                <span>370+ {t("landing.happyUsers")}</span>
                <span>•</span>
                <span>14-day {t("landing.freeTrial")}</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <img
                src="/images/real-time-fitness-studio-dashboard.png"
                alt="Studio Management Dashboard - View of class schedule, client bookings, and business analytics"
                className="rounded-lg shadow-2xl"
                loading="eager"
                width="600"
                height="400"
                itemProp="image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
              <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 px-3 py-2 rounded-lg text-sm text-gray-700">
                 {t("landing.realTimeDashboard")}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 bg-white" itemScope itemType="https://schema.org/ItemList">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" itemProp="name">
              {t("landing.featuresSectionTitle")}
            </h2>
            <p className="text-lg text-gray-600">
              {t("landing.featuresSectionDescription")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                itemScope
                itemType="https://schema.org/Service"
                itemProp="itemListElement"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" itemProp="name">
                  {feature.title}
                </h3>
                <p className="text-gray-600" itemProp="description">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50" itemScope itemType="https://schema.org/ItemList">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6" itemProp="name">
                {t("landing.benefitsSectionTitle")}
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                    itemScope
                    itemType="https://schema.org/ListItem"
                    itemProp="itemListElement"
                  >
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700" itemProp="name">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <img
                src="/images/benefits.png"
                alt="Business Analytics Dashboard - View of revenue reports, class attendance, and client retention metrics"
                className="rounded-lg shadow-2xl" 
                loading="lazy"
                width="600"
                height="400"
                itemProp="image"
              />
              <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 px-3 py-2 rounded-lg text-sm text-gray-700">
                {t("landing.trackYourStudioPerformance")}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Studios Section */}
      <section className="py-20 bg-white" itemScope itemType="https://schema.org/ItemList">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" itemProp="name">{t("landing.featuredStudiosTitle")}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("landing.featuredStudiosIntro")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Studio 1 */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" itemScope itemType="https://schema.org/LocalBusiness" itemProp="itemListElement">
              <div className="h-64 bg-gray-200 relative">
                <img
                  src="/images/raum-fur-haltung-bewegung-und-balance.webp"
                  alt="Raum für Haltung, Bewegung und Balance in Rahnsdorf - Physiotherapy practice using FitCalendar"
                  className="w-full h-full object-cover"
                  itemProp="image"
                />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Building className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium text-gray-500" itemProp="category">{t("landing.physiotherapyPractice")}</span>
                </div>
                <h3 className="text-2xl font-bold mb-3" itemProp="name">Raum für Haltung, Bewegung und Balance in Rahnsdorf</h3>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span itemProp="address">Rahnsdorf, Berlin</span>
                  <span>•</span>
                  <span itemProp="telephone">+49 30 1234567</span>
                </div>
                <a
                  href="https://fitcalendar.praxipro.io/studio/raum-fur-haltung-bewegung-und-balance/"
                  target="_blank"
                  className="inline-flex items-center text-primary font-medium hover:underline"
                  itemProp="url"
                >
                  {t("landing.onlineBooking")} <Link className="ml-1 h-4 w-4" />
                </a>
              </CardContent>
            </Card>

            {/* Studio 2 */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" itemScope itemType="https://schema.org/LocalBusiness" itemProp="itemListElement">
              <div className="h-64 bg-gray-200 relative">
                <img
                  src="/images/physiotherapie-karlheinz-grubel.webp"
                  alt="Physiotherapie Karlheinz Grübel - Professional physiotherapy studio using FitCalendar"
                  className="w-full h-full object-cover"
                  itemProp="image"
                />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Building className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium text-gray-500" itemProp="category">{t("landing.physiotherapyPractice")}</span>
                </div>
                <h3 className="text-2xl font-bold mb-3" itemProp="name">Physiotherapie Karlheinz Grübel</h3>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span itemProp="address">Berlin, Germany</span>
                  <span>•</span>
                  <span itemProp="telephone">+49 30 9876543</span>
                </div>
                <a
                  href="https://fitcalendar.praxipro.io/studio/physiotherapie-karlheinz-grubel/"
                  target="_blank"
                  className="inline-flex items-center text-primary font-medium hover:underline"
                  itemProp="url"
                >
                  {t("landing.onlineBooking")} <Link className="ml-1 h-4 w-4" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50" itemScope itemType="https://schema.org/ItemList">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" itemProp="name">{t("landing.howFitCalendarWorksTitle")}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("landing.howFitCalendarWorksIntro")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="itemListElement">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3" itemProp="name">{t("landing.howFitCalendarWorksStep1Title")}</h3>
              <p className="text-gray-600" itemProp="text">
                {t("landing.howFitCalendarWorksStep1Intro")}
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="itemListElement">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3" itemProp="name">{t("landing.howFitCalendarWorksStep2Title")}</h3>
              <p className="text-gray-600" itemProp="text">
                {t("landing.howFitCalendarWorksStep2Intro")}
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="itemListElement">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3" itemProp="name">{t("landing.howFitCalendarWorksStep3Title")}</h3>
              <p className="text-gray-600" itemProp="text">
                {t("landing.howFitCalendarWorksStep3Intro")}
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center" itemScope itemType="https://schema.org/HowToStep" itemProp="itemListElement">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-3" itemProp="name">{t("landing.howFitCalendarWorksStep4Title")}</h3>
              <p className="text-gray-600" itemProp="text">
                {t("landing.howFitCalendarWorksStep4Intro")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* As Seen Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">FitCalendar Reviews</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">

            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="text-center">
              {FitCalendarBadge()}
            </div>
            <div className="text-center">
              <a href="https://www.capterra.com/p/10031398/FitCalendar/" target="_blank" rel="noopener noreferrer"><img alt="Capterra Reviews" width="180px" src="/images/capterra-reviews.png" /></a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary text-white" itemScope itemType="https://schema.org/CallToAction">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6" itemProp="name">
            {t("landing.ctaSectionTitle")}
          </h2>
          <p className="text-lg mb-8 opacity-90" itemProp="description">
            {t("landing.ctaSectionDescription")}
          </p>
          <Button
            size="lg"
            variant="outline"
            className="bg-white text-primary hover:bg-gray-100"
            onClick={() => setIsContactOpen(true)}
            itemProp="potentialAction"
            itemScope
            itemType="https://schema.org/Action"
          >
            <span itemProp="name">{t("landing.ctaText")}</span>
          </Button>
        </div>
      </section>

      <ContactDialog
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </AdminPublicLayout>
  );
};

export default Index;
